import { A } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import type { PostAuthAction } from "@storytell/auth";
import { TbDownload } from "solid-icons/tb";
import { createSignal, Show } from "solid-js";
import { DownloadStartedModal } from "~/components/shared/DownloadStartedModal";
import { PlatformIcon } from "~/components/shared/PlatformIcon";
import { useI18n } from "~/i18n";
import { chiefDownloadHref, hasChiefBuild } from "~/lib/chiefDownload";
import { useOS } from "~/lib/useOS";

type Size = "sm" | "md" | "lg";

const SIZE_CLASS: Record<Size, string> = {
  sm: "gap-1.5 rounded-lg px-3.5 py-1.5 text-sm",
  md: "w-full gap-2 rounded-lg px-4 py-2.5 text-sm",
  lg: "gap-2 rounded-xl px-6 py-3 text-base",
};

const VARIANT_CLASS = {
  primary: "bg-brand text-on-brand hover:bg-brand-hover",
  secondary: "border border-primary-border bg-canvas text-primary-text hover:bg-panel",
  soft: "bg-brand/8 text-brand hover:bg-brand/14 dark:bg-brand/25 dark:text-[#c3cff5] dark:hover:bg-brand/35",
};

type Variant = keyof typeof VARIANT_CLASS;

const BASE_CLASS =
  "inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas";

/**
 * Links to signup. A `postAuthAction` rides the navigation state, so the paid
 * plan picked on a marketing page reaches checkout once the account exists.
 */
export function GetStartedButton(props: { variant: Variant; size: Size; postAuthAction?: PostAuthAction }) {
  const { t } = useI18n();
  return (
    <A
      href="/auth/signup"
      state={props.postAuthAction ? { postAuthAction: props.postAuthAction } : undefined}
      class={`${BASE_CLASS} ${SIZE_CLASS[props.size]} ${VARIANT_CLASS[props.variant]}`}
    >
      {t("marketing.cta.get_started")}
    </A>
  );
}

export function DownloadPageLink(props: { size: Size }) {
  const { t } = useI18n();
  return (
    <A href="/download" class={`${BASE_CLASS} ${SIZE_CLASS[props.size]} ${VARIANT_CLASS.primary}`}>
      <TbDownload class={props.size === "sm" ? "size-3.5 shrink-0" : "size-4 shrink-0"} aria-hidden="true" />
      {t("marketing.cta.download")}
    </A>
  );
}

/**
 * The installer for the visitor's OS, with the install walkthrough opened
 * beside it. An OS without a build gets Get started as the primary action
 * instead. The server and the first client render assume a Mac, since the OS
 * resolves only after mount. `os` pins one platform's installer regardless of
 * the visitor's.
 */
export function DownloadButton(props: { from: string; size: Size; os?: "mac" | "windows"; variant?: Variant }) {
  const { t } = useI18n();
  const detected = useOS();
  const os = () => props.os ?? detected();
  const [showDownloadStarted, setShowDownloadStarted] = createSignal(false);
  const href = () => chiefDownloadHref(os());

  const label = () => {
    if (props.size === "sm") return t("marketing.cta.download");
    return os() === "windows" ? t("marketing.cta.download_windows") : t("marketing.cta.download_mac");
  };

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    void stAnalytics.track("click_tracking", { cta: "live-capture-download", value: props.from, screen: undefined });
    setShowDownloadStarted(true);
  };

  return (
    <Show
      when={hasChiefBuild(os())}
      fallback={<GetStartedButton variant={props.variant ?? "primary"} size={props.size} />}
    >
      <a
        href={href()}
        onClick={onClick}
        class={`${BASE_CLASS} ${SIZE_CLASS[props.size]} ${VARIANT_CLASS[props.variant ?? "primary"]}`}
      >
        <PlatformIcon os={os()} class={props.size === "sm" ? "size-3.5 shrink-0" : "size-4 shrink-0"} />
        {label()}
      </a>
      <DownloadStartedModal
        open={showDownloadStarted()}
        href={href()}
        os={os()}
        onClose={() => setShowDownloadStarted(false)}
      />
    </Show>
  );
}
